import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from "@reduxjs/toolkit";
import queryString from "query-string";
import {
  Subscription,
  SUBSCRIPTION_PLAN,
  SUBSCRIPTION_STATUS,
} from "../models/subscription";
import {
  makeBackendGetCallWithJsonResponse,
  makeBackendPostCallWithJsonResponse,
} from "../utils/fetch";
import {
  ADD_ESSENTIALS_SUBSCRIPTION_PLAN,
  GET_STRIPE_CHECKOUT_SESSION,
  GET_STRIPE_PORTAL_SESSION,
  GET_SUBSCRIPTION_STATUS,
  STRIPE_SUBSCRIPTION_CUSTOMER,
  STRIPE_SUBSCRIPTION_SUCCESS,
} from "../utils/routes";
import { receiveErrors } from "./errorStore";
import { AccountType } from "./dashboard";

export interface createStripeCustomerProps {
  billing_email: string;
  name?: string;
}

export const createStripeCustomer = createAsyncThunk(
  STRIPE_SUBSCRIPTION_CUSTOMER,
  async (args: createStripeCustomerProps, thunkAPI) => {
    const result = await makeBackendPostCallWithJsonResponse(
      STRIPE_SUBSCRIPTION_CUSTOMER,
      args,
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export interface getSubscriptionStatusParams {
  refetch_subs_status: boolean;
  account_type?: AccountType | null;
}

export const getSubscriptionStatus = createAsyncThunk(
  GET_SUBSCRIPTION_STATUS,
  async (
    {
      refetch_subs_status,
      account_type = AccountType.ENGINEER,
    }: getSubscriptionStatusParams,
    thunkAPI,
  ) => {
    const queryParam = queryString.stringify({
      refetch_subs_status: refetch_subs_status ? true : false,
      account_type,
    });
    const result = await makeBackendGetCallWithJsonResponse<Subscription>(
      GET_SUBSCRIPTION_STATUS,
      `?${queryParam}`,
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

interface StripeCheckoutSessionResponse {
  checkout_url: string;
}

export const getStripeCheckoutSession = createAsyncThunk(
  GET_STRIPE_CHECKOUT_SESSION,
  async (
    {
      plan,
      refund_fees,
      force_no_trial,
      selected_account_type = AccountType.ENGINEER,
    }: {
      plan: SUBSCRIPTION_PLAN;
      refund_fees: boolean;
      force_no_trial?: boolean;
      selected_account_type?: AccountType;
    },
    thunkAPI,
  ) => {
    const result =
      await makeBackendPostCallWithJsonResponse<StripeCheckoutSessionResponse>(
        GET_STRIPE_CHECKOUT_SESSION,
        { plan, refund_fees, force_no_trial, selected_account_type },
      );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export const handleStripeCheckoutSuccess = createAsyncThunk(
  STRIPE_SUBSCRIPTION_SUCCESS,
  async (
    args: { session_id: string; selected_account_type: AccountType },
    thunkAPI,
  ) => {
    const result = await makeBackendPostCallWithJsonResponse<Subscription>(
      STRIPE_SUBSCRIPTION_SUCCESS,
      args,
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

interface GetStripePortalSessionResponse {
  portal_url: string;
}

export const getStripePortalSession = createAsyncThunk(
  GET_STRIPE_PORTAL_SESSION,
  async (_, thunkAPI) => {
    const result =
      await makeBackendPostCallWithJsonResponse<GetStripePortalSessionResponse>(
        GET_STRIPE_PORTAL_SESSION,
        {},
      );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export interface AddEssentialSubscriptionPlan {
  account_type?: number;
}

export const addBasicSubscriptionPlan = createAsyncThunk(
  ADD_ESSENTIALS_SUBSCRIPTION_PLAN,
  async (args: AddEssentialSubscriptionPlan, thunkAPI) => {
    const result = await makeBackendPostCallWithJsonResponse<Subscription>(
      ADD_ESSENTIALS_SUBSCRIPTION_PLAN,
      args,
    );
    if (result.success) {
      return result.resultJson;
    }
    const errors = { errors: result.resultJson };
    thunkAPI.dispatch(receiveErrors(errors));
    return thunkAPI.rejectWithValue(errors);
  },
);

export interface SubscriptionState extends Subscription {
  loading: boolean;
}

const initialState: SubscriptionState = {
  subscription_plan_choice: SUBSCRIPTION_PLAN.NO_SUBSCRIPTION_PLAN,
  stripe_subscription_status: SUBSCRIPTION_STATUS.NOT_DEFINED,
  loading: false,
};

export const subscriptionSlice = createSlice({
  name: "subscription store",
  initialState,
  reducers: {
    // For mocking in storybook.
    addTestPlan: (state, action: PayloadAction<Subscription>) => {
      state.subscription_plan_choice = action.payload.subscription_plan_choice;
      state.stripe_subscription_status =
        action.payload.stripe_subscription_status;
    },
    clearSubscription: (state) => {
      state.stripe_subscription_status =
        initialState.stripe_subscription_status;
      state.subscription_plan_choice = initialState.subscription_plan_choice;
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      isAnyOf(
        addBasicSubscriptionPlan.pending,
        handleStripeCheckoutSuccess.pending,
        getSubscriptionStatus.pending,
      ),
      (state) => {
        state.loading = true;
      },
    );
    builder.addMatcher(
      isAnyOf(
        addBasicSubscriptionPlan.rejected,
        handleStripeCheckoutSuccess.rejected,
        getSubscriptionStatus.rejected,
      ),
      (state) => {
        state.loading = false;
      },
    );
    builder.addMatcher(
      isAnyOf(
        addBasicSubscriptionPlan.fulfilled,
        handleStripeCheckoutSuccess.fulfilled,
        getSubscriptionStatus.fulfilled,
      ),
      (state, action) => {
        state.loading = false;
        const subscription = action.payload;
        if (
          state.stripe_subscription_status ===
            subscription.stripe_subscription_status &&
          state.subscription_plan_choice ===
            subscription.subscription_plan_choice
        ) {
          return state;
        }
        state.stripe_subscription_status =
          subscription.stripe_subscription_status;
        state.subscription_plan_choice = subscription.subscription_plan_choice;
      },
    );
  },
});

export const { addTestPlan, clearSubscription } = subscriptionSlice.actions;
export default subscriptionSlice.reducer;
